import {
    Users,
    Wallet,
    Mail,
    Layers,
    ShieldCheck,
    CircleHelp,
} from "lucide-react";

/** @typedef {{ q: string, a: string }} FaqItem */
/** @typedef {{ id: string, title: string, description: string, icon: import("react").ComponentType<{ className?: string }>, items: FaqItem[] }} FaqCategory */

/** @type {FaqCategory[]} */
export const FAQ_CATEGORIES = [
    {
        id: "recipients",
        title: "Recipients & status badges",
        description: "What the labels next to names mean on your pool dashboard.",
        icon: Users,
        items: [
            {
                q: "What does “Awaiting acceptance” mean?",
                a: "The person was invited by email but has not accepted the pool invitation yet. Their share is reserved on the pool, but they are not fully active until they open the invite link or accept from Invite Center. The badge uses a mail icon because the invite is still outstanding—not because your email is wrong.",
            },
            {
                q: "What does “Payout setup needed” mean?",
                a: "This person has a FairShare account linked to the pool (including you as owner, or someone who joined via invite) but has not linked a bank account on this pool member row yet. Use the ⋯ menu to add bank details—the form uses your country setting (UK sort code + account by default; US routing + account if you chose United States in Settings → Preferences). Stripe Connect setup under Settings → Profile may also be required for transfers.",
            },
            {
                q: "What does “Needs bank account” mean?",
                a: "This is an external recipient—someone without a FairShare login on the pool. They only need bank details stored on their member row (via Add bank account in the menu). No invite acceptance is required.",
            },
            {
                q: "What does “Ready for payout” mean?",
                a: "A bank account is on file for that member (you will see masked bank details under their name). They still may need Stripe payout setup in Profile if they receive via Connect, but the pool has the bank record required to run a payout.",
            },
            {
                q: "Why don’t I see “Verified” anymore?",
                a: "Older versions used a single Verified flag for both “accepted invite” and “bank linked,” which was confusing. Badges now reflect the actual state: invite pending, payout setup, external bank needed, or ready.",
            },
            {
                q: "I’m the owner—why am I not “Ready for payout”?",
                a: "Owners are FairShare users too. Until you add a bank account for your member row (or complete Connect setup where required), you will see Payout setup needed. That is normal and does not mean your account email is unverified.",
            },
        ],
    },
    {
        id: "pools",
        title: "Pools & allocations",
        description: "Creating pools, editing shares, and removing members.",
        icon: Layers,
        items: [
            {
                q: "Why must percentages total 100%?",
                a: "Each payout splits the amount by share. Active and pending-invite members on a submitted allocation must add up to 100%. Removed members are sent with remove: true and are not counted in that total.",
            },
            {
                q: "What happens when I remove someone?",
                a: "Mark them for removal in Edit allocations, confirm the removal, then submit. After approval (if required), they are detached from the pool and their invite is revoked. Remaining active shares are rebalanced to 100%.",
            },
            {
                q: "Can I replace members in one change?",
                a: "Yes. Remove existing recipients, add replacements with the desired percentages, and submit once. If you invite by email, invites are sent after the change is approved.",
            },
            {
                q: "Who can edit allocations?",
                a: "Workspace owners and admins can edit allocations and manage recipients. Members typically have read-only access to pools they belong to.",
            },
        ],
    },
    {
        id: "invitations",
        title: "Invitations",
        description: "Pool invites, workspace invites, and acceptance.",
        icon: Mail,
        items: [
            {
                q: "Where do invitees accept a pool invite?",
                a: "New users can register from the invite link. Existing users should sign in with the same email the invite was sent to, then accept from Invite Center or the link.",
            },
            {
                q: "What if the invite email already has an account?",
                a: "They must sign in and accept from Invite Center—the registration link will not create a duplicate account.",
            },
            {
                q: "When are invite emails sent?",
                a: "For changes that require approval, emails go out after an admin approves the allocation update. Pool creation sends invites once the pool is created.",
            },
            {
                q: "Can I resend or revoke an invite?",
                a: "Use Invite Center to view sent invites. Pending invites can be revoked; ask the recipient to check spam or request a new invite if delivery failed.",
            },
        ],
    },
    {
        id: "payouts",
        title: "Payouts & banks",
        description: "Running payouts, balances, and recipient readiness.",
        icon: Wallet,
        items: [
            {
                q: "Why is Run payout blocked?",
                a: "Every recipient with a share needs a bank on file on their member row. FairShare members may also need Stripe payout receiving setup under Settings → Profile when your workspace uses Connect transfers. The execute dialog lists who is blocking and why.",
            },
            {
                q: "Do external recipients need FairShare accounts?",
                a: "No. Add their bank details via the Recipients menu. They are labeled Needs bank account until that is done.",
            },
            {
                q: "Why do I see sort code instead of routing number?",
                a: "FairShare defaults to UK banking. Set your country under Settings → Preferences to switch labels and examples (for example United States shows routing number and Chase-style bank names). The link-bank dialog also lets you pick a country per recipient.",
            },
            {
                q: "What is available balance?",
                a: "Funds credited to the pool (e.g. from client payments) minus amounts already paid out. You cannot execute more than the available balance.",
            },
            {
                q: "Do payouts require approval?",
                a: "Depending on your workspace rules, payout execution may queue for admin approval like other sensitive actions. Check Approvals for pending payout requests.",
            },
        ],
    },
    {
        id: "approvals",
        title: "Approvals",
        description: "When changes need a second pair of eyes.",
        icon: ShieldCheck,
        items: [
            {
                q: "What requires approval?",
                a: "Allocation updates (percentages, adds, removes, invites), role changes, schedule changes, and payout execution—when your workspace is configured to require it.",
            },
            {
                q: "Who can approve?",
                a: "Owners and admins with approval permission. You cannot approve your own request.",
            },
            {
                q: "What happens after approval?",
                a: "The change is applied to the pool. For allocation updates with new invitees, invitation emails are sent after approval completes.",
            },
            {
                q: "Can I delete a pool with pending approvals?",
                a: "No. Resolve or reject open approvals first, then delete.",
            },
            {
                q: "What happens to a Business subscription if the owner is deleted?",
                a: "Workspace subscriptions belong to the organization, not the personal user row. If a superadmin deletes the workspace owner, FairShare deletes that owner's workspaces and immediately cancels any active Stripe subscription on those orgs so billing does not continue. If the user was only a member or admin of someone else's workspace, that workspace and its subscription are not deleted.",
            },
            {
                q: "Something is wrong with my account — what should I do?",
                a: "Try signing out and back in, check Invite Center for pending workspace invites, and confirm payout/bank setup under Settings. Workspace owners can use Billing → sync subscription if payment succeeded but the plan did not update. For locked-out accounts or corrupted access, contact your workspace owner or FairShare support; platform superadmins can reset passwords, adjust roles, or remove users from the admin console.",
            },
        ],
    },
    {
        id: "getting-started",
        title: "Getting started",
        description: "Workspaces, roles, and where to find things.",
        icon: CircleHelp,
        items: [
            {
                q: "What is a workspace?",
                a: "A workspace (organization) holds pools, billing, and team members. Switch workspaces from the header switcher next to the logo.",
            },
            {
                q: "What is the difference between admin and member?",
                a: "Admins and owners manage pools, billing, approvals, and audit. Members see dashboards for pools they belong to and can accept invites and manage their own payout profile.",
            },
            {
                q: "Where do I change my profile or 2FA?",
                a: "Open the avatar menu → Settings. Billing and audit (for admins) are also in that menu.",
            },
            {
                q: "Where is the audit log?",
                a: "Admins can open Audit from the main nav or profile menu to see a timeline of workspace actions.",
            },
        ],
    },
];

export function getFaqCategory(categoryId) {
    return FAQ_CATEGORIES.find((c) => c.id === categoryId) ?? null;
}
